import { IFont } from './fonts';
import PAIRINGS from './lineHeightPairings.json';

export interface ITypeRole {
  size: keyof IFont['size'];
  weight: keyof IFont['weight'];
  letterSpacing?: string;
}

export type TypeRole = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body-lg' | 'label-lg' | 'body-md' | 'label-md' | 'body-sm' | 'label-sm' | 'nav-item' | 'nav-item-semibold' | 'overline' | 'caption';

/**
 * Figma's type roles (Kickfin - Design System › Typography, node 1346:1452), as token keys.
 * A role's line-height is the pairing for its size (see roleLineHeight). Every role is set in Euclid. Code's H1–H4 components do not match the roles of the same
 * name yet; the type re-scale (KICK-5199) moves them over with their call sites.
 */
export const TYPE_ROLES: Record<TypeRole, ITypeRole> = {
  h1: { size: '6xl', weight: 'light' },
  h2: { size: '5xl', weight: 'regular' },
  h3: { size: '4xl', weight: 'semiBold' },
  h4: { size: '3xl', weight: 'semiBold' },
  h5: { size: '2xl', weight: 'semiBold' },
  h6: { size: 'xl', weight: 'semiBold' },
  'body-lg': { size: 'lg', weight: 'regular' },
  'label-lg': { size: 'lg', weight: 'semiBold' },
  'body-md': { size: 'md', weight: 'regular' },
  'label-md': { size: 'md', weight: 'semiBold' },
  'body-sm': { size: 'xs', weight: 'regular' },
  'label-sm': { size: 'xs', weight: 'semiBold' },
  // Figma marks the nav item roles chrome only: nav and filter pills, not form labels.
  'nav-item': { size: 'sm', weight: 'medium' },
  'nav-item-semibold': { size: 'sm', weight: 'semiBold' },
  // Figma's "tracking 8", read as its default unit (percent); pending design confirmation.
  overline: { size: 'xxs', weight: 'medium', letterSpacing: '0.08em' },
  caption: { size: 'xxs', weight: 'regular' },
};

/** A role's line-height token: the Figma pairing for its font size, as lineHeight() resolves it. */
export const roleLineHeight = (role: TypeRole): keyof IFont['lineHeight'] => (PAIRINGS as Record<keyof IFont['size'], keyof IFont['lineHeight']>)[TYPE_ROLES[role].size];
